import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { selectIsSystem, useAuthStore } from '../../stores';
import './Admin.css';

interface AIProvider {
  id: number;
  name: string;
  type: string;
  apiKey: string;
  hasApiKey?: boolean;
  apiKeyRevealed?: boolean;
  baseUrl?: string;
  isActive: boolean;
  maxTokens?: number;
  temperature?: number;
  description?: string;
  hasModule?: boolean;
  moduleFileName?: string | null;
  moduleClassName?: string | null;
}

interface LlmModuleInfo {
  type: string;
  label: string;
  hasModule: boolean;
  moduleFileName?: string | null;
  moduleClassName?: string | null;
}

interface ModuleSource {
  type: string;
  fileName: string;
  relativePath: string;
  source?: string | null;
  error?: string | null;
}

interface FormData {
  name: string;
  type: string;
  apiKey: string;
  baseUrl: string;
  isActive: boolean;
  maxTokens: number;
  temperature: number;
  description: string;
}

const emptyForm = (type = 'openai'): FormData => ({
  name: '',
  type,
  apiKey: '',
  baseUrl: '',
  isActive: true,
  maxTokens: 2000,
  temperature: 0.7,
  description: '',
});

export function AIProviderDetail() {
  const { providerId } = useParams<{ providerId: string }>();
  const [searchParams] = useSearchParams();
  const isNew = !providerId || providerId === 'new';
  const navigate = useNavigate();
  const { accessToken } = useAuthStore();
  const isSystem = useAuthStore(selectIsSystem);

  const [provider, setProvider] = useState<AIProvider | null>(null);
  const [modules, setModules] = useState<LlmModuleInfo[]>([]);
  const [moduleSource, setModuleSource] = useState<ModuleSource | null>(null);
  const [formData, setFormData] = useState<FormData>(() =>
    emptyForm(searchParams.get('type') || 'openai')
  );
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'failed'>('idle');
  const [showRevealedKey, setShowRevealedKey] = useState(false);

  const selectedModule = useMemo(
    () => modules.find((m) => m.type === formData.type) ?? null,
    [modules, formData.type]
  );

  const loadModules = useCallback(async () => {
    const res = await fetch('/api/admin/ai-provider-modules', {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!res.ok) throw new Error('Modül kataloğu yüklenemedi');
    setModules(await res.json());
  }, [accessToken]);

  const loadProvider = useCallback(async () => {
    if (isNew || !providerId) return;
    const res = await fetch(`/api/admin/ai-providers/${providerId}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.message || 'AI provider bulunamadı');
    }
    const data = (await res.json()) as AIProvider;
    setProvider(data);
    setFormData({
      name: data.name,
      type: data.type,
      apiKey: '',
      baseUrl: data.baseUrl || '',
      isActive: data.isActive,
      maxTokens: data.maxTokens || 2000,
      temperature: data.temperature ?? 0.7,
      description: data.description || '',
    });
  }, [accessToken, isNew, providerId]);

  const loadModuleSource = useCallback(
    async (type: string) => {
      const mod = modules.find((m) => m.type === type);
      if (!mod?.hasModule) {
        setModuleSource(null);
        return;
      }

      const res = await fetch(`/api/admin/ai-provider-modules/${encodeURIComponent(type)}/source`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (res.status === 404) {
        setModuleSource(null);
        return;
      }
      if (!res.ok) {
        setModuleSource({
          type,
          fileName: mod.moduleFileName || '',
          relativePath: '',
          error: 'Kaynak dosya okunamadı',
        });
        return;
      }
      setModuleSource(await res.json());
    },
    [accessToken, modules]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setError('');
      try {
        await loadModules();
        if (!isNew) {
          setLoading(true);
          await loadProvider();
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Yükleme başarısız');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isNew, loadModules, loadProvider]);

  useEffect(() => {
    if (modules.length === 0) return;
    void loadModuleSource(formData.type);
  }, [formData.type, modules, loadModuleSource]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      const url = isNew
        ? '/api/admin/ai-providers'
        : `/api/admin/ai-providers/${providerId}`;
      const method = isNew ? 'POST' : 'PUT';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Kaydetme başarısız');
      }

      const saved = (await response.json()) as AIProvider;
      if (isNew) {
        navigate(`/admin/ai-providers/${saved.id}`, { replace: true });
      } else {
        setProvider(saved);
        setFormData((prev) => ({ ...prev, apiKey: '' }));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kaydetme başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!provider || isNew) return;
    if (deleteConfirmName.trim() !== provider.name) {
      setError('Silmek için provider adını tam olarak yazın.');
      return;
    }

    setDeleting(true);
    setError('');
    try {
      const response = await fetch(`/api/admin/ai-providers/${provider.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Silme başarısız');
      }
      navigate('/admin/ai-providers', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Silme başarısız');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return <div className="admin-loading">Yükleniyor...</div>;
  }

  const typeLabel = selectedModule?.label || formData.type;
  const hasModule = Boolean(selectedModule?.hasModule);

  return (
    <div className="admin-page provider-detail-page">
      <header className="admin-page-header">
        <div>
          <p className="admin-breadcrumb">
            <Link to="/admin/ai-providers">AI Providers</Link>
            <span aria-hidden="true"> / </span>
            <span>{isNew ? 'Yeni' : provider?.name || providerId}</span>
          </p>
          <h1>{isNew ? 'Yeni AI Provider' : provider?.name}</h1>
          <div className="provider-detail-meta">
            <span className="provider-type-badge">{typeLabel}</span>
            <span
              className={`provider-module-badge ${
                hasModule ? 'provider-module-badge-ok' : 'provider-module-badge-missing'
              }`}
            >
              {hasModule ? selectedModule?.moduleFileName || 'Modül var' : 'Modül yok'}
            </span>
            <span
              className={`provider-status-pill ${
                formData.isActive ? 'provider-status-pill-on' : 'provider-status-pill-off'
              }`}
            >
              {formData.isActive ? 'Aktif' : 'Pasif'}
            </span>
          </div>
        </div>
        <Link className="admin-btn admin-btn-secondary" to="/admin/ai-providers">
          ← Listeye dön
        </Link>
      </header>

      {error && <div className="admin-error">{error}</div>}

      <div className="admin-detail-grid provider-detail-grid">
        <section className="admin-card provider-detail-form-card">
          <div className="admin-card-header">
            <h2>{isNew ? 'Provider oluştur' : 'Provider ayarları'}</h2>
          </div>
          <div className="admin-card-body">
            <form onSubmit={handleSubmit} className="admin-form provider-detail-form">
              <div className="provider-detail-section">
                <h3 className="provider-detail-section-title">Kimlik</h3>
                <div className="admin-form-group">
                  <label className="admin-form-label">
                    Provider adı *
                    <input
                      type="text"
                      className="admin-form-input"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      placeholder="Örn: OpenAI Production"
                    />
                  </label>
                </div>

                <div className="admin-form-row">
                  <div className="admin-form-group">
                    <label className="admin-form-label">
                      Tip *
                      <select
                        className="admin-form-select"
                        value={formData.type}
                        onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                        required
                      >
                        {(modules.length > 0
                          ? modules
                          : [{ type: formData.type, label: formData.type, hasModule: false }]
                        ).map((type) => (
                          <option key={type.type} value={type.type}>
                            {type.label}
                            {type.hasModule ? '' : ' (modül yok)'}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                  <div className="admin-form-group provider-detail-active-field">
                    <span className="admin-form-label">Durum</span>
                    <label className="admin-setting-checkbox">
                      <input
                        type="checkbox"
                        checked={formData.isActive}
                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      />
                      <span>Aktif</span>
                    </label>
                  </div>
                </div>

                <div className="admin-form-group">
                  <label className="admin-form-label">
                    Açıklama
                    <textarea
                      className="admin-form-textarea"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={2}
                      placeholder="İsteğe bağlı kısa not"
                    />
                  </label>
                </div>
              </div>

              <div className="provider-detail-section">
                <h3 className="provider-detail-section-title">Bağlantı</h3>
                <div className="admin-form-group">
                  <label className="admin-form-label">
                    API key {isNew ? '*' : ''}
                    <input
                      type="password"
                      className="admin-form-input"
                      value={formData.apiKey}
                      onChange={(e) => setFormData({ ...formData, apiKey: e.target.value })}
                      required={isNew}
                      placeholder={isNew ? 'sk-...' : 'Değiştirmek için yeni key girin'}
                      autoComplete="new-password"
                    />
                    {!isNew && (
                      <small className="admin-form-hint">
                        {provider?.hasApiKey
                          ? 'Değiştirmek istemiyorsanız boş bırakın'
                          : 'API key tanımlı değil'}
                      </small>
                    )}
                  </label>
                </div>

                {!isNew && provider?.hasApiKey && (
                  <div className="admin-form-group">
                    <div className="admin-form-label">Kayıtlı API key</div>
                    {isSystem && provider.apiKeyRevealed ? (
                      <div className="provider-api-key-reveal">
                        <code className="admin-mono provider-api-key-value">
                          {showRevealedKey ? provider.apiKey : '••••••••••••••••'}
                        </code>
                        <div className="provider-api-key-actions">
                          <button
                            type="button"
                            className="admin-btn admin-btn-secondary admin-btn-sm"
                            onClick={() => setShowRevealedKey((v) => !v)}
                          >
                            {showRevealedKey ? 'Gizle' : 'Göster'}
                          </button>
                          <button
                            type="button"
                            className="admin-btn admin-btn-secondary admin-btn-sm"
                            onClick={async () => {
                              try {
                                await navigator.clipboard.writeText(provider.apiKey);
                                setCopyStatus('copied');
                              } catch {
                                setCopyStatus('failed');
                              }
                              window.setTimeout(() => setCopyStatus('idle'), 2000);
                            }}
                          >
                            {copyStatus === 'copied'
                              ? 'Kopyalandı'
                              : copyStatus === 'failed'
                                ? 'Kopyalanamadı'
                                : 'Kopyala'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="provider-api-key-reveal">
                        <code className="admin-mono provider-api-key-value">
                          {provider.apiKey || '••••••••'}
                        </code>
                        {!isSystem && (
                          <small className="admin-form-hint">
                            Tam anahtarı yalnızca system rolü görebilir / kopyalayabilir.
                          </small>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <div className="admin-form-group">
                  <label className="admin-form-label">
                    Base URL
                    <input
                      type="url"
                      className="admin-form-input"
                      value={formData.baseUrl}
                      onChange={(e) => setFormData({ ...formData, baseUrl: e.target.value })}
                      placeholder="https://api.openai.com/v1"
                    />
                  </label>
                </div>
              </div>

              <div className="provider-detail-section">
                <h3 className="provider-detail-section-title">Üretim parametreleri</h3>
                <div className="admin-form-row">
                  <div className="admin-form-group">
                    <label className="admin-form-label">
                      Max tokens
                      <input
                        type="number"
                        className="admin-form-input"
                        value={formData.maxTokens}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            maxTokens: parseInt(e.target.value, 10) || 2000,
                          })
                        }
                        min={1}
                        max={32000}
                      />
                    </label>
                  </div>
                  <div className="admin-form-group">
                    <label className="admin-form-label">
                      Temperature
                      <input
                        type="number"
                        className="admin-form-input"
                        value={formData.temperature}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            temperature: parseFloat(e.target.value) || 0.7,
                          })
                        }
                        min={0}
                        max={2}
                        step={0.1}
                      />
                    </label>
                  </div>
                </div>
              </div>

              {!hasModule && (
                <div className="admin-warning-banner">
                  Bu tip için LLM client modülü yok. Kayıt tutulabilir; chat/inference çağrıları bu tip
                  seçiliyken çalışmaz.
                </div>
              )}

              <div className="admin-form-actions">
                <button
                  type="submit"
                  className="admin-btn admin-btn-primary"
                  disabled={saving || deleting}
                >
                  {saving ? 'Kaydediliyor...' : isNew ? 'Oluştur' : 'Kaydet'}
                </button>
              </div>
            </form>
          </div>
        </section>

        <aside className="admin-card provider-detail-module-card">
          <div className="admin-card-header admin-card-header-row">
            <h2>LLM modül kaynağı</h2>
            {hasModule ? (
              <span className="provider-module-badge provider-module-badge-ok">Hazır</span>
            ) : (
              <span className="provider-module-badge provider-module-badge-missing">Eksik</span>
            )}
          </div>
          <div className="admin-card-body">
            {hasModule ? (
              <>
                <dl className="provider-detail-module-meta">
                  <div>
                    <dt>Dosya</dt>
                    <dd className="admin-mono">
                      {moduleSource?.relativePath || selectedModule?.moduleFileName}
                    </dd>
                  </div>
                  {selectedModule?.moduleClassName && (
                    <div>
                      <dt>Sınıf</dt>
                      <dd className="admin-mono">{selectedModule.moduleClassName}</dd>
                    </div>
                  )}
                </dl>
                {moduleSource?.error && <div className="admin-error">{moduleSource.error}</div>}
                {moduleSource?.source ? (
                  <pre className="admin-code-panel provider-detail-code">
                    <code>{moduleSource.source}</code>
                  </pre>
                ) : (
                  !moduleSource?.error && (
                    <p className="admin-muted-text">Kaynak yükleniyor…</p>
                  )
                )}
              </>
            ) : (
              <div className="provider-detail-module-empty">
                <p>
                  <code className="admin-code-inline">{formData.type}</code> için henüz bir client
                  modülü yok.
                </p>
                <p className="admin-muted-text">
                  Beklenen konum:{' '}
                  <code className="admin-code-inline">Services/LlmProviders/*LlmChatClient.cs</code>
                </p>
              </div>
            )}
          </div>
        </aside>
      </div>

      {!isNew && provider && (
        <section className="admin-card admin-danger-zone provider-detail-danger">
          <div className="admin-card-header">
            <h2>Tehlikeli alan</h2>
          </div>
          <div className="admin-card-body">
            <p className="admin-muted-text">
              Bu provider silinince bağlı prompt’lar da etkilenir. Geri alınamaz.
            </p>
            {!showDeleteConfirm ? (
              <button
                type="button"
                className="admin-btn admin-btn-danger"
                onClick={() => {
                  setShowDeleteConfirm(true);
                  setDeleteConfirmName('');
                  setError('');
                }}
              >
                Provider’ı sil…
              </button>
            ) : (
              <div className="admin-delete-confirm">
                <label className="admin-form-label">
                  Onay için provider adını yazın: <strong>{provider.name}</strong>
                  <input
                    type="text"
                    className="admin-form-input"
                    value={deleteConfirmName}
                    onChange={(e) => setDeleteConfirmName(e.target.value)}
                    placeholder={provider.name}
                    autoFocus
                  />
                </label>
                <div className="admin-form-actions">
                  <button
                    type="button"
                    className="admin-btn admin-btn-secondary"
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setDeleteConfirmName('');
                    }}
                    disabled={deleting}
                  >
                    Vazgeç
                  </button>
                  <button
                    type="button"
                    className="admin-btn admin-btn-danger"
                    onClick={() => void handleDelete()}
                    disabled={deleting || deleteConfirmName.trim() !== provider.name}
                  >
                    {deleting ? 'Siliniyor...' : 'Kalıcı olarak sil'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
